import SidebarCollapse from "@builder/components/app/SidebarCollapse";
import { EditableProps } from "@builder/components/editable/editableProps";
import SelectInput from "@builder/components/general/SelectInput";
import TextInput from "@builder/components/general/TextInput";
import Toggle from "@builder/components/general/Toggle";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { BuilderSliceActions, setActiveNode, updateNodeProperty } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { SimpleCartNode } from "@common/build/types";
import "./style.scss";

type OpenMode = "overlayLeft" | "overlayRight";

const SimpleCart = ({ parent, excluded }: EditableProps) => {
    const simpleCartNode = useAppSelector(getSelectedNode) as SimpleCartNode | undefined;
    const activeNode = useAppSelector(state => state.builder.activeNode);
    const dispatch = useAppDispatch();
    const handleShowTaxes = (val: boolean) => {
        dispatch(BuilderSliceActions.updateSimpleCartShowTaxes(val));
    };
    const active = simpleCartNode?.id === activeNode;

    let activeElem = null;
    // let cartModeElem = null;
    let openModeElem = null;
    let quantityElem = null;
    let cartNameElem = null;
    let showTotalElem = null;
    if (!excluded?.includes("fixed")) {
        //Active

        const activeCallback = (checked: boolean) => {
            dispatch(setActiveNode(checked ? simpleCartNode?.id : null));
        };
        activeElem = <Toggle label="Show active" checked={active} callback={activeCallback} />;

        const openModeOptions: Record<OpenMode, string> = {
            overlayLeft: "Overlay from left",
            overlayRight: "Overlay from right"
        };
        const openMode = simpleCartNode?.expandedNode.openMode;
        const openModeCallback = (value: string) => {
            dispatch(updateNodeProperty({ propertyPath: ["expandedNode", "openMode"], value }));
        };
        openModeElem = <SelectInput title="Open mode" layout="column" value={openMode} options={openModeOptions} callback={openModeCallback} />;

        //Quantity
        const showQuantity = simpleCartNode?.expandedNode.showQuantity;
        const handleShowQuantity = (checked: boolean) => {
            dispatch(updateNodeProperty({ propertyPath: ["expandedNode", "showQuantity"], value: checked }));
        };
        quantityElem = <Toggle callback={handleShowQuantity} checked={showQuantity || false} label="Show quantity in cart button" />;

        //Show Totals
        const showTotal = simpleCartNode?.expandedNode.showTotal;
        const handleShowTotal = (checked: boolean) => {
            dispatch(updateNodeProperty({ propertyPath: ["expandedNode", "showTotal"], value: checked }));
        };
        showTotalElem = <Toggle callback={handleShowTotal} checked={showTotal || false} label="Show total in cart button" />;

        //Cart name
        cartNameElem = (
            <TextInput
                title="Cart name"
                placeholder="Cart"
                value={simpleCartNode ? simpleCartNode.expandedNode.name : ""}
                callback={(value: string) => dispatch(BuilderSliceActions.updateSimpleCartExpandedName(value))}
            />
        );
    }

    return (
        <SidebarCollapse title="Summary" parent={parent}>
            {activeElem}
            {openModeElem}
            {/* {activeElem || (openModeElem && <div className="editable-divider" />)} */}
            {!active && (
                <>
                    {quantityElem}
                    {showTotalElem}
                </>
            )}
            {(simpleCartNode?.name === "unfixedCart" || active) && (
                <>
                    {cartNameElem}
                    <Toggle
                        callback={(checked: boolean) => {
                            dispatch(updateNodeProperty({ propertyPath: ["expandedNode", "showCartSubtotal"], value: checked }));
                        }}
                        checked={simpleCartNode?.expandedNode.showCartSubtotal || false}
                        label="Show cart subtotal"
                    />
                    {simpleCartNode?.expandedNode.showCartSubtotal && (
                        <TextInput
                            value={simpleCartNode?.expandedNode.customizeCartSubtotal || ""}
                            callback={(value: string) => {
                                dispatch(updateNodeProperty({ propertyPath: ["expandedNode", "customizeCartSubtotal"], value }));
                            }}
                            title="Cart subtotal label"
                        />
                    )}
                    <Toggle
                        callback={(checked: boolean) => {
                            dispatch(updateNodeProperty({ propertyPath: ["expandedNode", "showCartDiscount"], value: checked }));
                        }}
                        checked={simpleCartNode?.expandedNode.showCartDiscount || false}
                        label="Show cart discount and fees"
                    />

                    <Toggle
                        callback={(checked: boolean) => {
                            dispatch(updateNodeProperty({ propertyPath: ["expandedNode", "showCartTotal"], value: checked }));
                        }}
                        checked={simpleCartNode?.expandedNode.showCartTotal || false}
                        label="Show cart total"
                    />

                    {simpleCartNode?.expandedNode.showCartTotal && (
                        <TextInput
                            value={simpleCartNode?.expandedNode.customizeCartTotal || ""}
                            callback={(value: string) => {
                                dispatch(updateNodeProperty({ propertyPath: ["expandedNode", "customizeCartTotal"], value }));
                            }}
                            title="Cart total label"
                        />
                    )}

                    <Toggle callback={handleShowTaxes} checked={simpleCartNode ? simpleCartNode.expandedNode.showTaxes : false} label="Show taxes" />
                    {simpleCartNode?.expandedNode.showTaxes && (
                        <TextInput
                            value={simpleCartNode?.expandedNode.customizeTaxes || ""}
                            callback={(value: string) => {
                                dispatch(updateNodeProperty({ propertyPath: ["expandedNode", "customizeTaxes"], value }));
                            }}
                            title="Taxes label"
                        />
                    )}

                    <Toggle
                        checked={simpleCartNode?.expandedNode.showBalance || false}
                        callback={(checked: boolean) => {
                            dispatch(updateNodeProperty({ propertyPath: ["expandedNode", "showBalance"], value: checked }));
                        }}
                        label="Show Balance"
                    />

                    {simpleCartNode?.expandedNode.showBalance && (
                        <TextInput
                            value={simpleCartNode?.expandedNode.balanceDueLabel || ""}
                            callback={(value: string) => {
                                dispatch(updateNodeProperty({ propertyPath: ["expandedNode", "balanceDueLabel"], value }));
                            }}
                            title="Balance label"
                        />
                    )}

                    <Toggle
                        checked={simpleCartNode?.expandedNode.showOrderTotal || false}
                        callback={(checked: boolean) => {
                            dispatch(updateNodeProperty({ propertyPath: ["expandedNode", "showOrderTotal"], value: checked }));
                        }}
                        label="Show order total"
                    />

                    {simpleCartNode?.expandedNode.showOrderTotal && (
                        <TextInput
                            value={simpleCartNode?.expandedNode.orderTotalLabel || ""}
                            callback={(value: string) => {
                                dispatch(updateNodeProperty({ propertyPath: ["expandedNode", "orderTotalLabel"], value }));
                            }}
                            title="Order total label"
                        />
                    )}
                </>
            )}
        </SidebarCollapse>
    );
};

export default SimpleCart;
